import { ResourceRequest } from "@root/data/lib";
import { API } from "./constants";
import { apiGateway } from "@root/lib/http";
import { FetchError } from "@root/lib/http/fetch-error";

// Request
export interface GitHubBillingPlanParams {
  installationId: string;
  accessTokenId: string;
  githubUserId: string;
}

// Reponse - Success
export interface SerializedGitHubBillingPlan {
  id: string;
  display_name: string;
  email: string;
  name: string;
  origin?: string;
  type: "user" | "org";
}
export interface DeserializedGitHubBillingPlan {
  id: string;
  displayName: string;
  email: string;
  name: string;
  origin?: string;
  type: "user" | "org";
}

// Response - Error
export interface SerializedGitHubBillingPlanError extends FetchError {
  body:
    | {
        type: "github-app:no-user-match";
      }
    | {
        type: "github-app:no-org-permission";
        data: {
          account_name: string;
        };
      };
}
export enum GitHubBillingPlanErrorType {
  UserMatchError,
  OrgPermissionError,
  BillingPlanError,
}
export type DeserializedGitHubBillingPlanError =
  | {
      type: GitHubBillingPlanErrorType.UserMatchError | GitHubBillingPlanErrorType.BillingPlanError;
    }
  | {
      type: GitHubBillingPlanErrorType.OrgPermissionError;
      orgName: string;
    };

export class GitHubBillingPlanRequest extends ResourceRequest<DeserializedGitHubBillingPlan, SerializedGitHubBillingPlan> {
  constructor(
    { installationId, accessTokenId, githubUserId }: GitHubBillingPlanParams,
    getData: () => DeserializedGitHubBillingPlan,
    setData: (error: DeserializedGitHubBillingPlanError, data: DeserializedGitHubBillingPlan) => void
  ) {
    super(
      apiGateway.patch<SerializedGitHubBillingPlan>(API.GITHUB_BILLING_PLANS, {
        params: { installationId },
        headers: {
          "access-token-id": accessTokenId,
          "github-user-id": githubUserId,
        },
      }),
      getData,
      (error, response) => {
        if (error) {
          return setData(this.deserializeError(error), null as any);
        }
        return setData(null as any, this.deserializeResponse(response!));
      }
    );
  }
  private deserializeError = (error: Error | SerializedGitHubBillingPlanError): DeserializedGitHubBillingPlanError => {
    if (!(error instanceof FetchError)) {
      return {
        type: GitHubBillingPlanErrorType.BillingPlanError,
      };
    }
    if (error.body.type === "github-app:no-user-match") {
      return {
        type: GitHubBillingPlanErrorType.UserMatchError,
      };
    }
    if (error.body.type === "github-app:no-org-permission") {
      return {
        type: GitHubBillingPlanErrorType.OrgPermissionError,
        orgName: error.body.data.account_name,
      };
    }
    return {
      type: GitHubBillingPlanErrorType.BillingPlanError,
    };
  };
  private deserializeResponse = ({
    display_name: displayName,
    ...preserialized
  }: SerializedGitHubBillingPlan): DeserializedGitHubBillingPlan => ({
    displayName,
    ...preserialized,
  });
}
